import { FC, ReactNode, KeyboardEvent, useEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { WarningCircleIcon, CheckCircleIcon, InfoIcon } from '@phosphor-icons/react';
import lureoBetaLogo from '../../assets/Lureo_Beta.png';
import { GoogleAuthButton } from './GoogleAuthButton';
import { AppleAuthButton } from './AppleAuthButton';
import { useEnvironmentPreferences } from '@/hooks/useEnvironmentPreferences';
import { useConsentOptional } from '@/modules/cookieConsent/useConsent';
import { ThemedLogo } from '@/modules/theme';
import { Input } from '@/components/cms/ui/input';
import { FieldLabel, FlowTitle } from '@/components/ui/text';
import { TextLink } from '@/components/ui/text-button';
import { forgetReturnPath, peekReturnPath } from '@/modules/auth/returnAfterLogin';
import { currentUserId } from '@/infrastructure/clients/BackendClient';

export const TERMS_OF_SERVICE_URL = 'https://www.lureo.com/terms';
export const PRIVACY_POLICY_URL = 'https://www.lureo.com/privacy-policy';

export type AuthStatusTone = 'error' | 'success' | 'info';

export interface AuthStatus {
  message: string;
  tone: AuthStatusTone;
}

interface AuthShellProps {
  title?: string;
  subtitle?: string;
  status?: AuthStatus | null;
  showOAuth?: boolean;
  marketingConsent?: boolean;
  canStartOAuth?: () => boolean;
  oauthTermsNote?: boolean;
  onError: (message: string) => void;
  children: ReactNode;
  footer?: ReactNode;
}

export const AuthShell: FC<AuthShellProps> = ({
  title,
  subtitle,
  status,
  showOAuth = true,
  marketingConsent,
  canStartOAuth,
  oauthTermsNote = false,
  onError,
  children,
  footer
}) => {
  const { environmentPreferences, failed: prefsFailed } = useEnvironmentPreferences();
  // Unknown after a failed read: shown, since hiding them leaves a Google or Apple user no way in
  const oauthOn = prefsFailed || Boolean(environmentPreferences?.google_auth_on);
  const consent = useConsentOptional();
  const bannerCoversFooter = Boolean(consent?.bannerOpen && !consent.hasConsented);

  return (
    <div className="h-screen supports-[height:100dvh]:h-dvh overflow-y-auto bg-lureo-emphasis-strong">
      <div
        className={`flex min-h-full flex-col items-center justify-start px-4 pb-6 pt-12 sm:pb-8 sm:pt-10 ${
          bannerCoversFooter ? 'pb-44 sm:pb-8' : ''
        }`}>
        <div className="flex w-full max-w-none flex-col items-center sm:max-w-sm">
          <span className="mb-6 w-full max-w-40 sm:max-w-44">
            <ThemedLogo src={lureoBetaLogo} alt="Lureo Logo" className="h-auto w-full" />
          </span>
          <div className="flex w-full flex-col gap-5 rounded-lureo-radius bg-lureo-backdrop p-5 shadow-lureo-shadow-sheet sm:gap-4 sm:p-4">
            {title && (
              <header className="flex flex-col gap-1">
                <FlowTitle as="h1">{title}</FlowTitle>
                {subtitle && <p className="font-lureo-font-prose text-lureo-14 font-light leading-normal text-lureo-ink-subtle">{subtitle}</p>}
              </header>
            )}
            {children}
            {showOAuth && oauthOn && (
              <>
                <div className="flex items-center gap-4">
                  <div className="h-px flex-1 bg-lureo-neutral-strong" />
                  <span className="text-lureo-12 tracking-[0.15em] text-lureo-ink-subtlest leading-4">OR</span>
                  <div className="h-px flex-1 bg-lureo-neutral-strong" />
                </div>
                <div className="flex flex-col gap-2">
                  <GoogleAuthButton onError={onError} marketingConsent={marketingConsent} canStart={canStartOAuth} />
                  <AppleAuthButton onError={onError} marketingConsent={marketingConsent} canStart={canStartOAuth} />
                </div>
                {oauthTermsNote && (
                  <p className="text-center font-lureo-font-prose text-lureo-12 font-light leading-4 text-lureo-ink-subtlest">
                    By continuing with Google or Apple, you agree to the{' '}
                    <TextLink size="sm" href={TERMS_OF_SERVICE_URL} target="_blank" rel="noopener noreferrer">Terms of Service</TextLink>
                    {' and '}
                    <TextLink size="sm" href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">Privacy Policy</TextLink>.
                  </p>
                )}
              </>
            )}
            {status && <AuthStatusMessage status={status} />}
            {footer}
          </div>
        </div>
      </div>
    </div>
  );
};

const AuthStatusMessage: FC<{ status: AuthStatus }> = ({ status }) => {
  const isError = status.tone === 'error';
  const Icon = isError ? WarningCircleIcon : status.tone === 'info' ? InfoIcon : CheckCircleIcon;

  return (
    <div
      role={isError ? 'alert' : 'status'}
      className={`flex items-start gap-2 rounded-lureo-radius border-lureo-border-width p-3 text-lureo-13 leading-snug ${
        isError
          ? 'border-lureo-danger/30 bg-lureo-danger/5 text-lureo-danger-ink'
          : 'border-lureo-brand-ink/25 bg-lureo-brand-ink/[0.12] text-lureo-brand-ink'
      }`}>
      <Icon className="mt-px h-4 w-4 shrink-0" weight="regular" />
      <span>{status.message}</span>
    </div>
  );
};

interface AuthFieldProps {
  id: string;
  label: string;
  type: 'text' | 'email' | 'password';
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
  placeholder?: string;
  disabled?: boolean;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  className?: string;
}

export const AuthField: FC<AuthFieldProps> = ({
  id,
  label,
  type,
  value,
  onChange,
  autoComplete,
  placeholder,
  disabled,
  onKeyDown,
  className
}) => (
  <div className={`flex min-w-0 flex-col gap-1.5 ${className ?? ''}`}>
    <FieldLabel htmlFor={id}>{label}</FieldLabel>
    <Input
      look="outlined"
      id={id}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      autoComplete={autoComplete}
      placeholder={placeholder}
      disabled={disabled}
    />
  </div>
);

interface AuthCrossLinkProps {
  prompt: string;
  to: string;
  action: string;
}

export const AuthCrossLink: FC<AuthCrossLinkProps> = ({ prompt, to, action }) => {
  const { search } = useLocation();

  return (
    <p className="text-center text-lureo-14 text-lureo-ink-subtle leading-5">
      {prompt}{' '}
      <TextLink asChild className="text-lureo-14">
        <Link to={{ pathname: to, search }}>{action}</Link>
      </TextLink>
    </p>
  );
};

export const SignedInRedirect = () => {
  const [to] = useState(() => peekReturnPath(currentUserId()) ?? '/dashboard');
  useEffect(forgetReturnPath, []);
  return <Navigate to={to} replace />;
};
